/**
 * The diary reduced to the handful of numbers a mood is made of.
 *
 * This exists so that the app and the push server cannot drift. The original
 * plan called for two implementations — one over Dexie, one in SQL — kept
 * honest by a parity test, which is a test that can only ever tell you *after*
 * the two have already disagreed. Instead the derivation lives here once, and
 * each side supplies only what it is genuinely better at: a dense day series.
 * Reading rows out of a store is where the two platforms legitimately differ;
 * deciding what "a struggling week" means is not.
 *
 * `days` is dense and ascending, one entry per calendar day, ending on
 * `today`. Days nobody logged are present with `kcal: 0` rather than missing —
 * a gap in the array and a gap in the diary have to be the same thing, and an
 * implementation that only returns rows it found would otherwise make a silent
 * fortnight look like a short attentive week.
 */

import { type DayOutcome, loggingStreak } from './targets.js'

/** One day, reduced. A `kcalTarget` of null means the day had no goal set. */
export interface VoiceDay {
  date: string
  kcal: number
  kcalTarget: number | null
}

/** How far back the callers are expected to read, and the reducer to look. */
export const VOICE_WINDOW_DAYS = 30
/** Over-budget days are counted within this trailing window. */
export const STRUGGLE_WINDOW_DAYS = 7
/** A streak this long, ended, is worth loyalty; shorter ones end all the time. */
export const BROKEN_STREAK_MIN_DAYS = 7
/** Same-weekday history is only worth a remark once there is this much of it. */
export const WEEKDAY_MIN_SAMPLES = 2

export interface VoiceFacts {
  /** 0 when `today` has an entry; otherwise how many days back the last one is. */
  daysSinceLastLog: number
  /** Over-budget days in the trailing 7, `today` inclusive. */
  overDaysInWeek: number
  /** A run of `BROKEN_STREAK_MIN_DAYS`+ that was alive yesterday and is not now. */
  streakRecentlyBroken: boolean
  /** Consecutive logged days ending at `today`; `today` may be empty. */
  currentStreak: number
  /** The day before `today`, or null when the window does not reach it. */
  yesterday: VoiceDay | null
  /**
   * How this weekday has gone historically, `today` excluded — the material
   * for "you are usually over on Fridays". `logged` under
   * `WEEKDAY_MIN_SAMPLES` means there is no pattern to speak of yet.
   */
  weekday: { logged: number; over: number }
}

const outcomeOf = (day: VoiceDay): DayOutcome => ({
  logged: day.kcal > 0,
  // A day with no target cannot be over one. The mood leans on the count of
  // over-days, and counting an ungoverned day as a failure would put someone
  // who never set a target permanently into loyal mode.
  withinTarget: day.kcalTarget == null || day.kcal <= day.kcalTarget,
})

const weekdayOf = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

export function voiceFacts(days: readonly VoiceDay[]): VoiceFacts {
  if (days.length === 0) {
    return {
      daysSinceLastLog: VOICE_WINDOW_DAYS,
      overDaysInWeek: 0,
      streakRecentlyBroken: false,
      currentStreak: 0,
      yesterday: null,
      weekday: { logged: 0, over: 0 },
    }
  }

  const today = days[days.length - 1]!
  const outcomes = days.map(outcomeOf)

  let daysSinceLastLog = VOICE_WINDOW_DAYS
  for (let i = days.length - 1; i >= 0; i--) {
    if (outcomes[i]?.logged === true) {
      daysSinceLastLog = days.length - 1 - i
      break
    }
  }

  const week = outcomes.slice(-STRUGGLE_WINDOW_DAYS)
  const overDaysInWeek = week.filter((o) => o.logged && !o.withinTarget).length

  // `loggingStreak` forgives an empty final day, so comparing the window with
  // and without today is what distinguishes "today is still young" from "the
  // run actually ended yesterday".
  const yesterdayStreak = loggingStreak(outcomes.slice(0, -1))
  const currentStreak = loggingStreak(outcomes)
  const streakRecentlyBroken = yesterdayStreak >= BROKEN_STREAK_MIN_DAYS && currentStreak === 0

  const todayWeekday = weekdayOf(today.date)
  const sameWeekday = days
    .slice(0, -1)
    .filter((day) => weekdayOf(day.date) === todayWeekday)
    .map(outcomeOf)
    .filter((o) => o.logged)

  return {
    daysSinceLastLog,
    overDaysInWeek,
    streakRecentlyBroken,
    currentStreak,
    yesterday: days.length >= 2 ? (days[days.length - 2] ?? null) : null,
    weekday: {
      logged: sameWeekday.length,
      over: sameWeekday.filter((o) => !o.withinTarget).length,
    },
  }
}

/**
 * Whether this weekday has enough history, and a bad enough record, to be
 * worth mentioning. Half the logged instances over budget is the bar — below
 * that it is a coincidence being sold as a pattern.
 */
export function weekdayIsWeak(facts: VoiceFacts): boolean {
  return (
    facts.weekday.logged >= WEEKDAY_MIN_SAMPLES && facts.weekday.over * 2 >= facts.weekday.logged
  )
}
